import { useTranslation } from 'react-i18next';
import { Link, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';

import { App, Button } from 'antd';
import {
  type BreadcrumbItemType,
  type BreadcrumbSeparatorType,
} from 'antd/lib/breadcrumb/Breadcrumb';
import type React from 'react';
import {
  type Key,
  type MouseEvent,
  createElement,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TruncatedTooltip from '../../../components/Common/TruncatedTooltip';
import { Icon } from '../../../components/Core/Icon';
import { iconComponents } from '../../../components/Core/IconImports';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { AppErrorCodeEnum } from '../../../enums/AppErrorCodeEnum';
import { AiClassificationStatusEnum } from '../../../enums/AiClassificationStatusEnum';
import { FileOrderColumnEnum } from '../../../enums/FilesOrderColumnEnum';
import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import type File from '../../../interfaces/File';
import type { FileUpdate } from '../../../interfaces/File';
import type { GetFilesPayload } from '../../../interfaces/FileApiService';
import type TableEmptyState from '../../../interfaces/TableEmptyState';
import { filteredFileActions } from '../../../lib/fileActions';
import { stripPreviewPath } from '../../../lib/previewRoutePath';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../../store/store';
import buildCollapsedBreadcrumbItems from '../../../utils/breadcrumbUtils';
import { isApiError } from '../../../utils/apiErrorCode';
import { handleError } from '../../../utils/errorsHandler';
import { tryOpenFilesImagePreview } from '../../../utils/filePreview';
import { handleFilePasswordError } from '../../../utils/filePasswordErrors';
import {
  areAllListedItemsImages,
  isImageFileExtension,
  removeShortcutExtension,
} from '../../../utils/utils';
import { useAuth } from '../../useAuth';
import { getAntTableScrollElement } from '../../useAntTableOverlayScrollbars';
import { useFileDownload } from '../../useFileDownload';
import useFilesActions from '../../useFilesActions';
import { useFileUpload } from '../../useFileUpload';
import useOpenFile from '../../useOpenFile';
import { usePermissions } from '../../usePermissions';
import { useRightPanel } from '../../useRightPanel';
import useTableHeight from '../../useTableHeight';
import { useTags } from '../../useTags';
import { useFilesRowSelection } from '../selection/useFilesSelection';
import { mapRecentFilesToRows } from '../../../services/fileApiService/mapRecentFiles';
import { fileService } from '../services';
import { getPartialFiltersBasedOnTable } from '../tableFilters';
import type { UseFilesProps } from '../types';

const RECENT_FILES_FETCH_LIMIT = 20;

/**
 * Owns API-backed list state, table UX (sort, scroll, context menus), breadcrumbs, selection,
 * folder-password flows, and store-driven side effects (uploads, global create events, cross-tab updates).
 *
 * @param props.table - Which logical files table (personal, trash, etc.) drives defaults and permissions.
 * @param props.folderUid - When set, list is scoped to that folder id in the route.
 */
export function useFilesListQuery({ table, folderUid }: UseFilesProps) {
  const { message, notification: notificationApi } = App.useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { onNewFileUploaded, offNewFileUploaded, setCurrentFolderUid } = useFileUpload();
  const { authenticatedUser, isReader, redirectLoggedInUser } = useAuth();
  const refreshToken = useAppStore((s) => s.customTokens?.rToken);
  const token = useAppStore((s) => s.customTokens?.token);
  const deniedSettings = useAppStore((s) => s.deniedSettings);
  const {
    getFilePasswordSession,
    setPendingFilePasswordRequest,
    setFileIdToNavigateAfterPassword,
    filePasswordSessions,
  } = useAppStore(
    useShallow((s) => ({
      getFilePasswordSession: s.getFilePasswordSession,
      setPendingFilePasswordRequest: s.setPendingFilePasswordRequest,
      setFileIdToNavigateAfterPassword: s.setFileIdToNavigateAfterPassword,
      filePasswordSessions: s.filePasswordSessions,
    })),
  );

  const { copyFileLinkToClipboard } = useOpenFile();
  const { tags } = useTags();
  const { isRightPanelOpen, openRightPanel } = useRightPanel();
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  /** Files used to filter context-menu actions (set synchronously on row contextmenu). */
  const [contextMenuTargetFiles, setContextMenuTargetFiles] = useState<File[]>([]);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
    direction: 'up' | 'down';
  }>({
    x: 0,
    y: 0,
    direction: 'down',
  });
  const [isFilesContextMenuVisible, setIsFilesContextMenuVisible] = useState(false);
  const [filesContextMenuPosition, setFilesContextMenuPosition] = useState<{
    x: number;
    y: number;
    direction: 'up' | 'down';
  }>({
    x: 0,
    y: 0,
    direction: 'down',
  });
  const lastManualTableScrollRef = useRef<number>(0);
  const pendingTableScrollTopTimeoutRef = useRef<number | null>(null);

  const { isAdminOrContributor, isInFileRoute } = useAuth();
  const { openFileSafely } = useOpenFile();
  const { handleDownloadFiles } = useFileDownload();
  const { selectedTags } = useTags();
  const filesActions = useFilesActions({
    folderUid,
  });
  const createFolderToken = useAppStore((state) => state.appEvents.fileCreation.createFolderToken);
  const createFileFromTemplateEvent = useAppStore(
    (state) => state.appEvents.fileCreation.createFileFromTemplate,
  );
  const createSecretFileEvent = useAppStore(
    (state) => state.appEvents.fileCreation.createSecretFile,
  );
  const createNoteFileToken = useAppStore(
    (state) => state.appEvents.fileCreation.createNoteFileToken,
  );
  const lastHandledCreateFolderTokenRef = useRef(createFolderToken);
  const lastHandledCreateFileFromTemplateSequenceRef = useRef(createFileFromTemplateEvent.sequence);
  const lastHandledCreateSecretFileSequenceRef = useRef(createSecretFileEvent.sequence);
  const lastHandledCreateNoteFileTokenRef = useRef(createNoteFileToken);
  /** Bumps on each `getFiles` effect run so out-of-order responses cannot overwrite paginated state. */
  const filesFetchGenerationRef = useRef(0);
  /** Blocks duplicate load-more before `isLoading` flips true (effect runs after paint). */
  const loadMoreLockedRef = useRef(false);
  /** Prevents re-opening the grid-mode suggestion for the same folder on WebSocket re-fetch. */
  const lastGridSuggestionContextRef = useRef<string | null>(null);

  const { setIsCurrentFolderReadOnly, isCurrentFolderReadOnly, decreaseReceivedUnreadCountBy } =
    usePermissions();

  const setIsUploadDisabledInCurrentFolder = useAppStore(
    (s) => s.setIsUploadDisabledInCurrentFolder,
  );

  // `prependFileId` (URL): highlight and pin a row after navigation (e.g. after upload).
  const [searchParams] = useSearchParams();
  const prependFileId = searchParams.get('prependFileId');
  const prependFileIdNum = prependFileId ? parseInt(prependFileId) : null;
  const [prependFileData, setPrependFileData] = useState<File | null>(null);

  const [selectedTableKeys, setSelectedTableKeys] = useState<Key[]>([]);
  const [files, setFiles] = useState<File[]>([]);

  const patchFilesMarkedReadLocally = useCallback((fileIds: number[]) => {
    if (fileIds.length === 0) return;
    const idSet = new Set(fileIds);
    setFiles((prev) => prev.map((f) => (idSet.has(f.id) ? { ...f, isUnRead: false } : f)));
    setPrependFileData((prev) =>
      prev && idSet.has(prev.id) ? { ...prev, isUnRead: false } : prev,
    );
  }, []);

  /** Marks received/shared unread rows as read: PATCHes the server, decrements the badge, clears `isUnRead` locally. */
  const markUnreadItemAsReadAndRefreshBadge = useCallback(
    (item: Pick<File, 'id' | 'isUnRead'>) => {
      if (!item.isUnRead) return;
      void fileService
        .markFilesAsRead([item.id])
        .then((res) => {
          decreaseReceivedUnreadCountBy(res.updated);
          patchFilesMarkedReadLocally([item.id]);
        })
        .catch(() => {});
    },
    [decreaseReceivedUnreadCountBy, patchFilesMarkedReadLocally],
  );

  const [parentFolder, setParentFolder] = useState<File | null>(null);
  const [totalFiles, setTotalFiles] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listFetchFailed, setListFetchFailed] = useState(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [filters, setFilters] = useState<GetFilesPayload>({
    offset: 0,
    count: 50,
    orderColumn: FileOrderColumnEnum.Title,
    orderAsc: true,
    list: folderUid ? 0 : getPartialFiltersBasedOnTable(table).list,
    type: getPartialFiltersBasedOnTable(table).type,
    parent: folderUid,
  });

  const rowSelection = useFilesRowSelection(selectedTableKeys, (keys) =>
    setSelectedTableKeys(keys),
  );

  // Grid vs list layout; persisted in `sessionStorage` for the session.
  const [isFilesListInGridMode, setIsFilesListInGridMode] = useState(() => {
    const storedValue = sessionStorage.getItem('isFilesListInGridMode');
    return storedValue === 'true';
  });

  useEffect(() => {
    lastGridSuggestionContextRef.current = null;
  }, [folderUid, table]);

  // Re-measure when the active view mounts (table body vs grid scroller).
  const { tableScrollY } = useTableHeight(70, !isFilesListInGridMode);
  const { tableScrollY: gridScrollY } = useTableHeight(
    70,
    isFilesListInGridMode,
    '[data-files-grid-scroller]',
  );

  const matchFilesSection = useMatch('/files/:section/*');
  const currentSection = matchFilesSection?.params?.section || 'personal';

  /**
   * Scrolls the Ant Design table body to the top after list-changing actions, without fighting
   * a scroll the user just performed: waits out a 2s quiet window from the last manual scroll.
   */
  const scrollToTopWhenIdle = useCallback(() => {
    const body = document.querySelector<HTMLElement>('.ant-table-body');
    const scrollElement = getAntTableScrollElement(body);
    if (!scrollElement) return;

    const now = Date.now();
    const sinceLast = now - lastManualTableScrollRef.current;

    const doScroll = () => scrollElement.scrollTo({ top: 0, behavior: 'smooth' });

    if (sinceLast >= 2000) {
      requestAnimationFrame(doScroll);
    } else {
      if (pendingTableScrollTopTimeoutRef.current) {
        clearTimeout(pendingTableScrollTopTimeoutRef.current);
      }
      pendingTableScrollTopTimeoutRef.current = window.setTimeout(
        () => requestAnimationFrame(doScroll),
        2000 - sinceLast,
      );
    }
  }, []);

  /**
   * Inserts newly created files that belong to the current parent at the top of the list and bumps `totalFiles`.
   */
  const onFilesCreated = (newFiles: File[]) => {
    const filteredFiles = newFiles.filter(
      (file) =>
        file.parentId === parentFolder?.id || (file.parentId === null && parentFolder === null),
    );
    setFiles((prevFiles) => [...filteredFiles, ...prevFiles]);
    setTotalFiles(totalFiles + filteredFiles.length);
    requestAnimationFrame(scrollToTopWhenIdle);
  };

  /** Prevent out-of-order AI updates from reopening terminal rows. */
  const shouldKeepExistingAiStatus = (currentFile: File, updatedFile: FileUpdate) => {
    if (updatedFile.aiOperationStatus == null) {
      return false;
    }

    const currentStatus = currentFile.aiOperationStatus;
    const nextStatus = updatedFile.aiOperationStatus;
    const currentIsTerminal =
      currentStatus === AiClassificationStatusEnum.Done ||
      currentStatus === AiClassificationStatusEnum.Error;
    const nextIsInProgress =
      nextStatus === AiClassificationStatusEnum.Queued ||
      nextStatus === AiClassificationStatusEnum.Classifying;

    return currentIsTerminal && nextIsInProgress;
  };

  /** Merges server-shaped rows into local state by matching `id`. */
  const onFilesUpdated = (updatedFiles: FileUpdate[]) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => {
        const updatedFile = updatedFiles.find((f) => f.id === file.id);
        if (!updatedFile) {
          return file;
        }

        if (shouldKeepExistingAiStatus(file, updatedFile)) {
          return file;
        }

        const merged = { ...file, ...updatedFile };
        if (updatedFile.avThreatName === undefined && file.avThreatName) {
          merged.avThreatName = file.avThreatName;
        }
        if (updatedFile.avScanError === undefined && file.avScanError) {
          merged.avScanError = file.avScanError;
        }
        return merged;
      }),
    );
    setPrependFileData((prev) => {
      if (!prev) return prev;
      const updatedFile = updatedFiles.find((f) => f.id === prev.id);
      if (!updatedFile) return prev;
      const merged = { ...prev, ...updatedFile };
      if (updatedFile.avThreatName === undefined && prev.avThreatName) {
        merged.avThreatName = prev.avThreatName;
      }
      if (updatedFile.avScanError === undefined && prev.avScanError) {
        merged.avScanError = prev.avScanError;
      }
      return merged;
    });
  };

  /**
   * Removes rows (moved, trashed, etc.), clears selection, and may reset `offset` to 0 when the
   * viewport would otherwise be empty while more items exist server-side.
   */
  const onFilesDeleted = (removedFiles: File[]) => {
    const removedIds = new Set(removedFiles.map((f) => f.id));
    setFiles((prev) => prev.filter((f) => !removedIds.has(f.id)));
    setSelectedTableKeys([]);
    setTotalFiles((prevTotal) => {
      const nextTotal = Math.max(0, prevTotal - removedIds.size);
      const nextVisibleCount = files.filter((f) => !removedIds.has(f.id)).length;
      if (nextVisibleCount === 0 && nextTotal > 0) {
        setFilters((prev) => ({ ...prev, offset: 0, refreshToken: Date.now() }));
      }
      return nextTotal;
    });
  };

  /**
   * Row `contextmenu`: ensures the row is selected, then positions the file action menu within the viewport.
   */
  const handleRowContextMenu = (record: File, event: MouseEvent) => {
    event.preventDefault();
    let menuTargetFiles: File[];
    if (!selectedTableKeys.includes(record.id)) {
      setSelectedTableKeys([record.id]);
      menuTargetFiles = [record];
    } else {
      menuTargetFiles = files.filter((file) => selectedTableKeys.includes(file.id));
      if (menuTargetFiles.length === 0) {
        menuTargetFiles = [record];
      }
    }
    setContextMenuTargetFiles(menuTargetFiles);
    const menuWidth = 220;
    let menuHeight = contextMenuFileActions.length * 32 + 24;
    const contextMenu = document.getElementsByClassName('file-context-menu')[0];
    if (contextMenu) {
      menuHeight = contextMenu.clientHeight;
    }
    let direction: 'up' | 'down' = 'down';
    let left: number;
    let top: number;
    if (event.clientX > window.innerWidth / 2) {
      left = event.clientX - menuWidth;
    } else {
      left = event.clientX;
    }
    if (event.clientY > window.innerHeight / 2) {
      top = event.clientY - menuHeight;
      direction = 'up';
    } else {
      top = event.clientY;
    }
    if (left < 10) left = 10;
    if (left + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 10;
    }
    if (top < 10) top = 10;
    if (top + menuHeight > window.innerHeight) {
      top = window.innerHeight - menuHeight - 10;
    }
    setContextMenuPosition({ x: left, y: top, direction });
    setIsContextMenuVisible(true);
  };

  /** Empty table area `contextmenu`: opens the bulk/new-item menu with viewport clamping. */
  const handleEmptyAreaContextMenu = (event: MouseEvent) => {
    event.preventDefault();
    const menuWidth = 180;
    let menuHeight = 200;
    let direction: 'up' | 'down' = 'down';
    let left: number;
    let top: number;

    if (event.clientX > window.innerWidth / 2) {
      left = event.clientX - menuWidth;
    } else {
      left = event.clientX;
    }

    if (event.clientY > window.innerHeight / 2) {
      top = event.clientY - menuHeight;
      direction = 'up';
    } else {
      top = event.clientY;
    }

    if (left < 10) left = 10;
    if (left + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 10;
    }
    if (top < 10) top = 10;
    if (top + menuHeight > window.innerHeight) {
      top = window.innerHeight - menuHeight - 10;
    }

    setFilesContextMenuPosition({ x: left, y: top, direction });
    setIsFilesContextMenuVisible(true);
  };

  /**
   * Navigates into a folder, or opens the password modal when the folder is locked and no session exists.
   * Marks unread received/shared rows as read when navigation proceeds.
   */
  const openFolderOrRequestPassword = useCallback(
    (record: File) => {
      if (!record.isFolder) return;
      if (record.isPasswordSet && !record.isPasswordBypass && !getFilePasswordSession(record.id)) {
        setFileIdToNavigateAfterPassword(record.id);
        setPendingFilePasswordRequest({
          fileId: record.id,
          fileName: record.fileName,
        });
        setIsNavigating(false);
        return;
      }
      markUnreadItemAsReadAndRefreshBadge(record);
      navigate(`/files/${currentSection}/folder/${record.id}`);
    },
    [
      currentSection,
      getFilePasswordSession,
      markUnreadItemAsReadAndRefreshBadge,
      setPendingFilePasswordRequest,
      setFileIdToNavigateAfterPassword,
      setIsNavigating,
      navigate,
    ],
  );

  /**
   * Image title click: open the shared thumbnail carousel when registered; otherwise fall back to viewer.
   */
  const openImageCarouselOrFile = (record: File): void => {
    const fileExtension = removeShortcutExtension(record.fileExtension ?? '');
    if (isImageFileExtension(fileExtension) && tryOpenFilesImagePreview(record.id)) {
      return;
    }
    openFileSafely(record);
  };

  /**
   * Filename cell click: folder → navigate (or trash restore flow); file → open or trash restore.
   * Non-filename cell → row selection only (`handleRowClick`).
   */
  const handleFileRowClick = (
    record: File,
    hasClickedOnFilename: boolean,
    event?: React.MouseEvent,
  ) => {
    if (hasClickedOnFilename && record.isFolder) {
      if (isInFileRoute(FilesTableEnum.Trash)) {
        filesActions.handleOpenFileFromTrash(record, {
          onFilesDeleted: onFilesDeleted,
        });
      } else {
        setSelectedTableKeys([]);
        if (files.length > 0) {
          setIsNavigating(true);
        }
        openFolderOrRequestPassword(record);
      }
    } else if (hasClickedOnFilename && !isInFileRoute(FilesTableEnum.Trash)) {
      // Filename open must select row so Details/Chat RightPanel sync has a file.
      // Transition: batch with the router's preview navigation into one render (not a blocking pass first).
      startTransition(() => setSelectedTableKeys([record.id]));
      openImageCarouselOrFile(record);
    } else if (hasClickedOnFilename && isInFileRoute(FilesTableEnum.Trash)) {
      filesActions.handleOpenFileFromTrash(record, {
        onFilesDeleted: onFilesDeleted,
      });
    } else {
      handleRowClick(record, event);
    }
  };

  /** Double-click: same navigation/open semantics as filename click, without the non-filename selection branch. */
  const handleFileRowDoubleClick = (record: File) => {
    if (record.isFolder) {
      if (isInFileRoute(FilesTableEnum.Trash)) {
        filesActions.handleOpenFileFromTrash(record, {
          onFilesDeleted: onFilesDeleted,
        });
      } else {
        if (files.length > 0) {
          setIsNavigating(true);
        }
        openFolderOrRequestPassword(record);
      }
    } else {
      if (!isInFileRoute(FilesTableEnum.Trash)) {
        openImageCarouselOrFile(record);
      } else {
        filesActions.handleOpenFileFromTrash(record, {
          onFilesDeleted: onFilesDeleted,
        });
      }
    }
  };

  /** Drag-and-drop move: delegates to `useFilesActions.moveFiles` with list refresh callbacks. */
  const handleMoveDraggedFiles = (files: File[], parentFolder: File | null) => {
    filesActions.moveFiles(parentFolder?.id ?? null, files, {
      onFilesDeleted: onFilesDeleted,
    });
  };

  /** Row click selection: supports Shift range and Ctrl/Cmd toggle; clears an open row context menu. */
  const handleRowClick = (record: File, event?: React.MouseEvent) => {
    const isShiftPressed = event?.shiftKey;
    const isCtrlPressed = event?.ctrlKey || event?.metaKey;
    let selectedRows: number[] = [];
    if (isShiftPressed && rowSelection.selectedRowKeys.length === 1) {
      const fileSelectedIndex = files.findIndex(
        (item) => rowSelection.selectedRowKeys[0] === item.id,
      );
      const fileClickedIndex = files.findIndex((item) => item.id === record.id);
      if (fileClickedIndex > fileSelectedIndex) {
        selectedRows = files.slice(fileSelectedIndex, fileClickedIndex + 1).map((f) => f.id);
      } else if (fileClickedIndex < fileSelectedIndex) {
        selectedRows = files.slice(fileClickedIndex, fileSelectedIndex + 1).map((f) => f.id);
      }
    } else if (isCtrlPressed) {
      if (rowSelection.selectedRowKeys.includes(record.id)) {
        selectedRows = rowSelection.selectedRowKeys
          .filter((key) => key !== record.id)
          .map((i) => parseInt(i as string));
      } else {
        selectedRows = [
          ...rowSelection.selectedRowKeys.map((i) => parseInt(i as string)),
          record.id,
        ];
      }
    } else {
      selectedRows = [record.id];
    }
    setSelectedTableKeys(selectedRows);
    setIsContextMenuVisible(false);
  };

  /**
   * Ant Design `Table` `onChange`: maps column keys to `FileOrderColumnEnum`, updates sort direction.
   * Resets `offset` only when sort actually changes (same as Users/Groups). Spurious onChange from
   * dataSource/scroll remounts must not wipe infinite-scroll offset back to 0.
   */
  const handleTableChange = (_: any, __: any, sorter?: any) => {
    let orderColumn = filters.orderColumn;
    let orderAsc = filters.orderAsc;
    let sortChanged = false;

    if (sorter && sorter.field) {
      switch (sorter.field) {
        case 'title':
          orderColumn = FileOrderColumnEnum.Title;
          break;
        case 'fileSize':
          orderColumn = FileOrderColumnEnum.Size;
          break;
        case 'lastModified':
        case 'deletedAt':
          orderColumn = FileOrderColumnEnum.ModifiedAt;
          break;
        default:
          break;
      }
      const newOrderAsc = sorter.order === 'ascend';
      sortChanged = orderColumn !== filters.orderColumn || newOrderAsc !== filters.orderAsc;
      orderAsc = newOrderAsc;
    }

    if (!sortChanged) {
      return;
    }

    handleFiltersChange({
      parent: folderUid,
      list: folderUid ? 0 : filters.list,
      offset: 0,
      orderColumn,
      orderAsc,
    });
  };

  /** Breadcrumb link: sets navigating flag then pushes the target folder path. */
  const handleBreadcrumbClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    setIsNavigating(true);
    navigate(path);
  };

  /** Builds linked breadcrumb items from the route section and `parentFolder.path` for the list header. */
  const rawBreadcrumbItems = useMemo(() => {
    let currentHref = `/files/${currentSection}`;
    let currentTitle = (
      <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
        {t('common.files')}
      </Link>
    );
    let currentTitleWithoutLink = t('common.files');
    switch (table) {
      case FilesTableEnum.Personal:
        currentTitle = (
          <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
            {t('common.files')}
          </Link>
        );
        currentTitleWithoutLink = t('common.files');
        break;
      case FilesTableEnum.Received:
        currentTitle = (
          <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
            {t('common.received')}
          </Link>
        );
        currentTitleWithoutLink = t('common.received');
        break;
      case FilesTableEnum.Shared:
        currentTitle = (
          <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
            {t('common.shared')}
          </Link>
        );
        currentTitleWithoutLink = t('common.shared');
        break;
      case FilesTableEnum.Favorites:
        currentTitle = (
          <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
            {t('common.favorites')}
          </Link>
        );
        currentTitleWithoutLink = t('common.favorites');
        break;
      case FilesTableEnum.Recent:
        currentTitle = (
          <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
            {t('common.recent')}
          </Link>
        );
        currentTitleWithoutLink = t('common.recent');
        break;
      case FilesTableEnum.Hidden:
        currentTitle = (
          <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
            {t('common.hidden')}
          </Link>
        );
        currentTitleWithoutLink = t('common.hidden');
        break;
      case FilesTableEnum.Trash:
        currentTitle = (
          <Link to={currentHref} onClick={(e) => handleBreadcrumbClick(e, currentHref)}>
            {t('common.trash')}
          </Link>
        );
        currentTitleWithoutLink = t('common.trash');
        break;
      default:
    }
    const items: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] = [
      {
        href: parentFolder?.path && parentFolder.path.length > 0 ? currentHref : undefined,
        title:
          parentFolder?.path && parentFolder.path.length > 0 ? (
            currentTitle
          ) : (
            <span className="inline-block max-w-[150px] overflow-clip align-middle leading-snug text-ellipsis whitespace-nowrap">
              <TruncatedTooltip>{currentTitleWithoutLink}</TruncatedTooltip>
            </span>
          ),
      },
    ];
    if (parentFolder) {
      parentFolder.path?.map((folder) => {
        const isLastElement = folder.uid === parentFolder.id;
        const folderPath = `${currentHref}/folder/${folder.uid}`;
        items.push({
          href: isLastElement ? undefined : `${currentHref}/${folder.uid}`,
          title: !isLastElement ? (
            <Link
              to={folderPath}
              onClick={(e) => handleBreadcrumbClick(e, folderPath)}
              className="inline-flex min-h-0 max-w-[150px] overflow-clip text-ellipsis"
            >
              {folder.title}
            </Link>
          ) : (
            <span className="inline-flex min-h-0 max-w-[150px] overflow-clip text-ellipsis whitespace-nowrap">
              <TruncatedTooltip>{folder.title}</TruncatedTooltip>
            </span>
          ),
        });
      });
    }
    return items;
  }, [t, table, parentFolder, handleBreadcrumbClick]);

  /** Collapses long paths for the header using `buildCollapsedBreadcrumbItems`. */
  const breadcrumbItems = useMemo<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[]>(() => {
    const items = rawBreadcrumbItems ?? [];
    return buildCollapsedBreadcrumbItems(items, { maxVisible: 3 }) ?? [];
  }, [rawBreadcrumbItems]);

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  /** Infinite scroll / "load more": bumps `filters.offset` when `hasMore` is true. */
  const handleLoadMore = useCallback(() => {
    if (isLoading || loadMoreLockedRef.current || !hasMoreRef.current) {
      return;
    }
    loadMoreLockedRef.current = true;
    setFilters((prev) => ({
      ...prev,
      offset: prev.offset + prev.count,
    }));
  }, [isLoading]);

  /** Merges partial filters; clears `prependFileId` from the URL when user-driven filters change. */
  const handleFiltersChange = (filters: Partial<GetFilesPayload>) => {
    setFilters((prev) => ({
      ...prev,
      ...filters,
    }));
    if (prependFileIdNum) {
      setPrependFileData(null);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('prependFileId');
      navigate(
        {
          pathname: location.pathname,
          search: newParams.toString(),
        },
        { replace: true },
      );
    }
  };

  /** Selected row models derived from `selectedTableKeys`. */
  const selectedFiles = useMemo(() => {
    return files.filter((file) => selectedTableKeys.includes(file.id));
  }, [files, selectedTableKeys]);

  /** Upload dropzone/read-only guard from table type, folder flags, and contributor role. */
  const isUploadDisabledInCurrentFolder = useMemo(() => {
    switch (table) {
      case FilesTableEnum.Personal:
      case FilesTableEnum.Received:
        if (filters.type === 1) {
          return !isCurrentFolderReadOnly;
        } else {
          return !isAdminOrContributor;
        }
      case FilesTableEnum.Shared:
      case FilesTableEnum.Favorites:
      case FilesTableEnum.Hidden:
        return !folderUid || !parentFolder || parentFolder.isReadOnly;
      case FilesTableEnum.Recent:
        return true;
      default:
        return true;
    }
  }, [filters, isCurrentFolderReadOnly, table]);

  useEffect(() => {
    setIsUploadDisabledInCurrentFolder(isUploadDisabledInCurrentFolder);
  }, [isUploadDisabledInCurrentFolder, setIsUploadDisabledInCurrentFolder]);

  /**
   * Stable subset of `filters` used as the `getFiles` effect dependency: refetches when request fields change,
   * not when unrelated object identity churns.
   */
  const memoizedFilters = useMemo<GetFilesPayload>(() => {
    return {
      ...filters,
    };
  }, [
    filters.offset,
    filters.parent,
    filters.onBehalfOf,
    filters.fileTypes,
    filters.shareWith,
    filters.sharedFrom,
    filters.tags,
    filters.orderAsc,
    filters.orderColumn,
    filters.count,
    filters.list,
    filters.onlySigned,
    filters.onlyUnsealed,
    filters.onlyExpired,
    filters.expiringWithinDays,
    filters.refreshToken,
  ]);

  /** Counts active filter dimensions (types, share targets, on-behalf-of, shared expiry). */
  const activeFiltersCount = useMemo(() => {
    return (
      (filters?.fileTypes?.length || 0) +
      (filters?.shareWith?.length || 0) +
      (filters?.sharedFrom?.length || 0) +
      (filters?.onBehalfOf !== undefined ? 1 : 0) +
      (filters?.onlyExpired === true ? 1 : 0) +
      (filters?.expiringWithinDays != null ? 1 : 0)
    );
  }, [filters]);

  const retryListFetch = useCallback(() => {
    setListFetchFailed(false);
    setFilters((prev) => ({
      ...prev,
      offset: 0,
      refreshToken: Date.now(),
    }));
  }, []);

  /** Icon + label for the Ant Design empty renderer (loading, filters, section-specific copy). */
  const emptyState: TableEmptyState = useMemo(() => {
    if (listFetchFailed && !isLoading) {
      return {
        label: t('common.filesFetchFailed'),
        iconName: 'TriangleAlertIcon' as keyof typeof iconComponents,
        variant: 'error',
        onRetry: retryListFetch,
      };
    }

    let iconName: string;
    if (activeFiltersCount > 0) {
      iconName = 'FolderSearchIcon';
    } else {
      switch (currentSection) {
        case 'personal':
          iconName = 'FolderIcon';
          break;
        case 'received':
          iconName = 'FileDownIcon';
          break;
        case 'shared':
          iconName = 'FileUserIcon';
          break;
        case 'favorites':
          iconName = 'StarIcon';
          break;
        case 'recent':
          iconName = 'HistoryIcon';
          break;
        case 'hidden':
          iconName = 'ArchiveIcon';
          break;
        case 'trash':
          iconName = 'TrashIcon';
          break;
        default:
          iconName = 'FilesIcon';
      }
    }
    let label: string;
    if (isLoading) {
      label = t('common.loadingFiles');
    } else if (activeFiltersCount > 0) {
      label = t('common.noFilesMatchingFilters');
    } else if (folderUid) {
      label = t('common.noFilesInThisFolder');
    } else {
      switch (currentSection) {
        case 'personal':
          label = t('common.dropHereYourFirstFile');
          break;
        case 'received':
          label = t('common.noFilesReceived');
          break;
        case 'shared':
          label = t('common.hereYouCanFindSharedFiles');
          break;
        case 'favorites':
          label = t('common.hereYouCanFindFavoritesFiles');
          break;
        case 'recent':
          label = t('common.noRecentlyOpenedFiles');
          break;
        case 'hidden':
          label = t('common.hereYouCanFindHiddenFiles');
          break;
        case 'trash':
          label = t('common.hereYouCanFindDeletedFiles');
          break;
        default:
          label = t('common.noFiles');
      }
    }
    return { label, iconName: iconName as keyof typeof iconComponents, variant: 'empty' };
  }, [
    currentSection,
    isLoading,
    listFetchFailed,
    retryListFetch,
    t,
    folderUid,
    isUploadDisabledInCurrentFolder,
    activeFiltersCount,
  ]);

  /** Shared fields for `filteredFileActions` (context menu vs top bar use different `files`). */
  const filesActionsContextBase = useMemo(
    () => ({
      authenticatedUser,
      isInFileRoute,
      isAdminOrContributor,
      isReader,
      isCurrentFolderReadOnly,
      tags,
      isRightPanelOpen,
      isUploadDisabledInCurrentFolder,
      getFilePasswordSession,
      deniedSettings: deniedSettings ?? null,
    }),
    [
      authenticatedUser,
      isInFileRoute,
      isAdminOrContributor,
      isReader,
      isCurrentFolderReadOnly,
      tags,
      isRightPanelOpen,
      isUploadDisabledInCurrentFolder,
      getFilePasswordSession,
      filePasswordSessions,
      deniedSettings,
    ],
  );

  /** Row context menu entries (uses `contextMenuTargetFiles` set on contextmenu). */
  const contextMenuFileActions = useMemo(() => {
    const menuFiles =
      contextMenuTargetFiles.length > 0 ? contextMenuTargetFiles : (selectedFiles ?? []);
    return filteredFileActions({ ...filesActionsContextBase, files: menuFiles });
  }, [filesActionsContextBase, contextMenuTargetFiles, selectedFiles]);

  /** Toolbar bulk actions (subset of the same filter pipeline with different flags). */
  const topBarFileActions = useMemo(
    () =>
      filteredFileActions({ ...filesActionsContextBase, files: selectedFiles ?? [] }, false, true),
    [filesActionsContextBase, selectedFiles],
  );

  useEffect(() => {
    if (!isContextMenuVisible) {
      setContextMenuTargetFiles([]);
    }
  }, [isContextMenuVisible]);

  /** Subscribes to global upload events to splice new or replaced rows into this list when they belong here. */
  useEffect(() => {
    const checkIfFileCanBeAdded = (file: File, isReplacing: boolean) => {
      if (isReplacing && !files.some((f) => f.id === file.id)) {
        return false;
      }
      if (
        !file.parentId &&
        !parentFolder &&
        !files.some((f) => f.id === file.id) &&
        isInFileRoute(FilesTableEnum.Personal)
      ) {
        return true;
      }
      if (file.parentId === parentFolder?.id && !files.some((f) => f.id === file.id)) {
        return true;
      }
      return false;
    };
    const handleNewFileUploaded = (file: File, isReplacing: boolean) => {
      if (checkIfFileCanBeAdded(file, isReplacing)) {
        if (isReplacing && files.some((f) => f.id === file.id)) {
          setFiles((prevFiles) => prevFiles.map((f) => (f.id === file.id ? { ...file } : f)));
        } else {
          setFiles((prevFiles) => [file, ...prevFiles]);
          setTotalFiles((prevCount) => prevCount + 1);
          scrollToTopWhenIdle();
        }
      }
    };
    onNewFileUploaded(handleNewFileUploaded);
    return () => {
      offNewFileUploaded(handleNewFileUploaded);
      if (pendingTableScrollTopTimeoutRef.current) {
        clearTimeout(pendingTableScrollTopTimeoutRef.current);
      }
    };
  }, [onNewFileUploaded, offNewFileUploaded]);

  /**
   * Loads the page via `fileService.getFiles(memoizedFilters)` (or `getRecentFiles` for Recent):
   * merges pages, updates parent metadata, and surfaces folder-password lockout / required flows.
   * Folder password session headers are attached in `httpClient`.
   */
  useEffect(() => {
    const fetchGeneration = ++filesFetchGenerationRef.current;
    setIsLoading(true);
    setListFetchFailed(false);

    const finishFetch = () => {
      if (fetchGeneration !== filesFetchGenerationRef.current) {
        return;
      }
      if (showUnlockMessagesRef.current) {
        showUnlockMessagesRef.current = false;
        message.destroy();
        message.success({
          content: t('notifications.folderUnlocked'),
        });
      }
      loadMoreLockedRef.current = false;
      setIsLoading(false);
      setIsNavigating(false);
    };

    if (table === FilesTableEnum.Recent) {
      fileService
        .getRecentFiles(RECENT_FILES_FETCH_LIMIT)
        .then((response) => {
          if (fetchGeneration !== filesFetchGenerationRef.current) {
            return;
          }
          // Mapper never throws on bad payloads; still guard so a future change cannot white-screen.
          const rows = mapRecentFilesToRows(response);
          setListFetchFailed(false);
          setFiles(rows);
          setTotalFiles(rows.length);
          setParentFolder(null);
          setHasMore(false);
          setIsCurrentFolderReadOnly(false);
        })
        .catch((error) => {
          if (fetchGeneration !== filesFetchGenerationRef.current) {
            return;
          }
          // Soft-fail: keep shell working (403/500 → retry; missing endpoint → empty).
          setFiles([]);
          setTotalFiles(0);
          setHasMore(false);
          setParentFolder(null);
          const status = error?.status as number | undefined;
          if (status === 401) {
            handleError(AppErrorCodeEnum.SessionExpired);
            return;
          }
          // Endpoint not deployed yet (dev API): empty state, no toast/Sentry noise.
          if (status === 404 || isApiError(error?.response, ApiErrorEnum.ObjectNotFound)) {
            setListFetchFailed(false);
            return;
          }
          setListFetchFailed(true);
          handleError(AppErrorCodeEnum.FilesFetchGeneric, {
            logToSentry: true,
            error,
          });
        })
        .finally(finishFetch);
      return;
    }

    const payload: GetFilesPayload = { ...memoizedFilters };
    if (payload.onlyExpired !== true) {
      delete payload.onlyExpired;
    }

    fileService
      .getFiles(payload)
      .then((response) => {
        if (fetchGeneration !== filesFetchGenerationRef.current) {
          return;
        }
        // Success HTTP status may still carry `{ name: FOLDER_PASSWORD_* , value }` instead of a file payload.
        if (response && typeof response === 'object') {
          const name = (response as any).name;
          if (
            name === ApiErrorEnum.FolderPasswordRequired ||
            name === ApiErrorEnum.FolderPasswordLockout
          ) {
            const syntheticError = {
              status: name === ApiErrorEnum.FolderPasswordLockout ? 429 : 403,
              response: response,
              config: { data: payload },
            };
            if (handleFilePasswordError(syntheticError)) {
              setFiles([]);
              setTotalFiles(0);
              setParentFolder(null);
              setHasMore(false);
              const lockoutInfo = useAppStore.getState().filePasswordLockoutInfo;
              if (lockoutInfo != null) {
                handleError(AppErrorCodeEnum.FilesFetchPasswordLockout, {
                  i18nParams: { minutes: lockoutInfo.minutesRemaining ?? 0 },
                  onNotify: redirectLoggedInUser,
                });
              }
            }
            return;
          }
        }
        if (response.error) {
          return;
        }
        setListFetchFailed(false);
        setFiles((prevFiles) => {
          const allFiles =
            payload.offset === 0 ? response.myFiles : [...prevFiles, ...response.myFiles];
          const filesById = new Map();
          allFiles.forEach((f) => filesById.set(f.id, f));
          return Array.from(filesById.values());
        });
        setTotalFiles(response.total);
        setParentFolder(response.parent);
        if (response.parent?.isReadOnly === false) {
          setIsCurrentFolderReadOnly(true);
        } else {
          setIsCurrentFolderReadOnly(false);
        }
        setHasMore(payload.offset + response.myFiles.length < (response.total ?? 0));
        if (payload.offset === 0) {
          try {
            const gridSuggestionContextKey = `suggest-grid-mode-${table}-${folderUid ?? 'root'}`;
            if (
              !isFilesListInGridMode &&
              areAllListedItemsImages(response.myFiles) &&
              lastGridSuggestionContextRef.current !== gridSuggestionContextKey
            ) {
              lastGridSuggestionContextRef.current = gridSuggestionContextKey;
              notificationApi.open({
                actions: createElement(
                  Button,
                  {
                    icon: createElement(Icon, {
                      className: 'h-4 w-4',
                      name: 'LayoutGridIcon',
                    }),
                    onClick: () => {
                      notificationApi.destroy(gridSuggestionContextKey);
                      setIsFilesListInGridMode(true);
                    },
                    size: 'small',
                    type: 'primary',
                  },
                  t('notifications.suggestGridMode.action'),
                ),
                description: t('notifications.suggestGridMode.description'),
                duration: 8,
                key: gridSuggestionContextKey,
                placement: 'topRight',
                title: t('notifications.suggestGridMode.title'),
              });
            }
          } catch {
            // ponytail: suggestion is best-effort; incomplete file rows / missing notification.open must not fail the list fetch
          }
        }
      })
      .catch((error) => {
        if (fetchGeneration !== filesFetchGenerationRef.current) {
          return;
        }
        if (handleFilePasswordError(error)) {
          setFiles([]);
          setTotalFiles(0);
          setParentFolder(null);
          setHasMore(false);
          const lockoutInfo = useAppStore.getState().filePasswordLockoutInfo;
          if (lockoutInfo != null) {
            handleError(AppErrorCodeEnum.FilesFetchPasswordLockout, {
              i18nParams: { minutes: lockoutInfo.minutesRemaining ?? 0 },
              onNotify: redirectLoggedInUser,
            });
          }
          return;
        }

        if (error.status === 401) {
          handleError(AppErrorCodeEnum.SessionExpired);
        } else {
          if (payload.offset === 0) {
            setFiles([]);
            setTotalFiles(0);
            setHasMore(false);
          }
          setListFetchFailed(true);
          handleError(AppErrorCodeEnum.FilesFetchGeneric, {
            logToSentry: true,
            error,
          });
        }
      })
      .finally(finishFetch);
  }, [memoizedFilters, table]);

  /** Recent: refetch when the tab becomes visible again (viewer openings land in the list). */
  useEffect(() => {
    if (table !== FilesTableEnum.Recent) {
      return;
    }
    const onVisibilityChange = () => {
      // window `focus` is too chatty (devtools/automation) and can pin the list in Loading.
      if (document.visibilityState !== 'visible') {
        return;
      }
      setFilters((prev) => ({
        ...prev,
        offset: 0,
        refreshToken: Date.now(),
      }));
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [table]);

  /**
   * After a folder password challenge clears, navigates or refetches so the list reflects the new session.
   * Skips work when the pending request originated from modals, upload, or the viewer (those flows own retry).
   */
  const pendingFilePasswordRequest = useAppStore((s) => s.pendingFilePasswordRequest);
  const hadPendingRequestRef = useRef(false);
  const requestedFileIdFromPendingRef = useRef<number | null>(null);
  /** Pending password flow came from move/shortcut/upload UI; this hook must not refetch or navigate. */
  const fromModalOrUploadRef = useRef(false);
  /** `'viewer'` when `getFileForViewer` set the pending state; viewer owns retry, so skip list refetch here. */
  const pendingSourceRef = useRef<'files' | 'viewer' | null>(null);
  /** Drives the transient "unlocking folder" loading message cleared in the `getFiles` `finally` block. */
  const showUnlockMessagesRef = useRef(false);

  useEffect(() => {
    if (pendingFilePasswordRequest) {
      hadPendingRequestRef.current = true;
      const requestedId = pendingFilePasswordRequest.requestedFileId;
      requestedFileIdFromPendingRef.current =
        requestedId != null ? requestedId : pendingFilePasswordRequest.fileId;
      const state = useAppStore.getState();
      fromModalOrUploadRef.current =
        state.pendingFilePasswordFromMoveForm || state.pendingFilePasswordFromUpload;
      pendingSourceRef.current = state.pendingFilePasswordSource;
    } else if (hadPendingRequestRef.current) {
      hadPendingRequestRef.current = false;
      if (pendingSourceRef.current === 'viewer') {
        pendingSourceRef.current = null;
        requestedFileIdFromPendingRef.current = null;
        return;
      }
      if (fromModalOrUploadRef.current) {
        fromModalOrUploadRef.current = false;
        requestedFileIdFromPendingRef.current = null;
        return;
      }
      const fileIdToNav = useAppStore.getState().fileIdToNavigateAfterPassword;
      if (
        fileIdToNav != null &&
        typeof fileIdToNav === 'number' &&
        getFilePasswordSession(fileIdToNav)
      ) {
        setFileIdToNavigateAfterPassword(null);
        const unlockedFolder = files.find((f) => f.id === fileIdToNav);
        if (unlockedFolder) {
          markUnreadItemAsReadAndRefreshBadge(unlockedFolder);
        }
        showUnlockMessagesRef.current = true;
        message.open({
          type: 'loading',
          content: t('notifications.unlockingFolder'),
          duration: 0,
        });
        navigate(`/files/${currentSection}/folder/${fileIdToNav}`);
      } else if (fileIdToNav === null) {
        const requestedId = requestedFileIdFromPendingRef.current;
        const currentParentId = memoizedFilters.parent
          ? parseInt(String(memoizedFilters.parent), 10)
          : NaN;
        const fileIdToRefetch = requestedId ?? (isNaN(currentParentId) ? null : currentParentId);
        if (fileIdToRefetch != null && getFilePasswordSession(fileIdToRefetch)) {
          showUnlockMessagesRef.current = true;
          message.open({
            type: 'loading',
            content: t('notifications.unlockingFolder'),
            duration: 0,
          });
          setFilters((prev) => ({
            ...prev,
            offset: 0,
            parent: String(fileIdToRefetch),
            refreshToken: Date.now(),
          }));
        }
        requestedFileIdFromPendingRef.current = null;
      } else {
        showUnlockMessagesRef.current = true;
        message.open({
          type: 'loading',
          content: t('notifications.unlockingFolder'),
          duration: 0,
        });
        setFilters((prev) => ({ ...prev, offset: 0, refreshToken: Date.now() }));
      }
    }
  }, [
    pendingFilePasswordRequest,
    setFilters,
    getFilePasswordSession,
    setFileIdToNavigateAfterPassword,
    navigate,
    currentSection,
    memoizedFilters.parent,
    message,
    t,
    files,
    markUnreadItemAsReadAndRefreshBadge,
  ]);

  /** Resolves `prependFileId` from the URL into either an in-list row or a targeted `getFiles` fetch. */
  useEffect(() => {
    if (!prependFileIdNum || isNaN(prependFileIdNum)) {
      setPrependFileData(null);
      return;
    }
    const found = files.find((f) => f.id === prependFileIdNum);
    if (found) {
      setFiles((prevFiles) => {
        if (prevFiles[0]?.id === prependFileIdNum && prevFiles[0]?.isUnRead) return prevFiles;
        const filtered = prevFiles.filter((f) => f.id !== prependFileIdNum);
        return [{ ...found, isUnRead: true }, ...filtered];
      });
      setPrependFileData(null);
    } else {
      fileService
        .getFiles({ ...memoizedFilters, ids: [prependFileIdNum.toString()] })
        .then((resp) => {
          const prependFile = resp.myFiles?.[0];
          if (prependFile) {
            setPrependFileData({ ...prependFile, isUnRead: true });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depends on `files` for local match; full deps would refetch too often
  }, [files, prependFileIdNum]);

  /** Once `prependFileData` is hydrated, splices the highlighted row to the top and reconciles totals. */
  useEffect(() => {
    if (prependFileData) {
      setFiles((prevFiles) => {
        const filtered = prevFiles.filter((f) => f.id !== prependFileData.id);
        return [prependFileData, ...filtered];
      });
      if (memoizedFilters.offset === 0) {
        setTotalFiles((prevCount) => {
          const alreadyPresent = files.some((f) => f.id === prependFileData.id);
          return alreadyPresent ? prevCount : prevCount + 1;
        });
        setHasMore(() => {
          const nextFilesLength =
            files.length + (files.some((f) => f.id === prependFileData.id) ? 0 : 1);
          return nextFilesLength >= filters.count;
        });
      }
      setPrependFileData(null);
    }
  }, [prependFileData, memoizedFilters.offset, filters.count, files]);

  /** Route folder id changed: reset list scope and pagination offset. */
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      list: folderUid ? 0 : getPartialFiltersBasedOnTable(table).list,
      parent: folderUid,
      offset: 0,
    }));
  }, [folderUid]);

  /** Tag picker changed: sync `filters.tags` and restart pagination from offset 0. */
  useEffect(() => {
    const newTagIds = selectedTags.map((tag) => tag.id).sort();
    const oldTagIds = (filters.tags || []).slice().sort();
    const areEqual =
      newTagIds.length === oldTagIds.length && newTagIds.every((id, idx) => id === oldTagIds[idx]);
    if (!areEqual) {
      setFilters((prev) => ({
        ...prev,
        tags: newTagIds,
        offset: 0,
      }));
    }
  }, [selectedTags, filters.tags]);

  /** Persists grid vs list toggle for the remainder of the browser session. */
  useEffect(() => {
    sessionStorage.setItem('isFilesListInGridMode', isFilesListInGridMode.toString());
  }, [isFilesListInGridMode]);

  /** Keeps upload context aligned with `parentFolder` so new uploads target the open directory. */
  useEffect(() => {
    if (parentFolder?.id) {
      setCurrentFolderUid(parentFolder.id);
    } else {
      setCurrentFolderUid(null);
    }
  }, [parentFolder]);

  /** Store `createFolderToken` edge: opens create-folder flow once per token bump. */
  useEffect(() => {
    if (createFolderToken === 0) {
      return;
    }
    if (createFolderToken === lastHandledCreateFolderTokenRef.current) {
      return;
    }
    lastHandledCreateFolderTokenRef.current = createFolderToken;
    filesActions.handleCreateFolder({
      onFilesCreated,
    });
  }, [createFolderToken, filesActions.handleCreateFolder, onFilesCreated]);

  /** Store template-creation event: opens templated file creation once per sequence change. */
  useEffect(() => {
    if (createFileFromTemplateEvent.sequence === 0 || !createFileFromTemplateEvent.template) {
      return;
    }
    if (
      createFileFromTemplateEvent.sequence === lastHandledCreateFileFromTemplateSequenceRef.current
    ) {
      return;
    }
    lastHandledCreateFileFromTemplateSequenceRef.current = createFileFromTemplateEvent.sequence;
    filesActions.handleCreateFileFromTemplate(createFileFromTemplateEvent.template, {
      onFilesCreated,
    });
  }, [
    createFileFromTemplateEvent.sequence,
    createFileFromTemplateEvent.template,
    filesActions.handleCreateFileFromTemplate,
    onFilesCreated,
  ]);

  /** Store secret-file event: opens secret file creation once per sequence bump. */
  useEffect(() => {
    if (createSecretFileEvent.sequence === 0) {
      return;
    }
    if (createSecretFileEvent.sequence === lastHandledCreateSecretFileSequenceRef.current) {
      return;
    }
    lastHandledCreateSecretFileSequenceRef.current = createSecretFileEvent.sequence;
    filesActions.handleCreateSecretFile(createSecretFileEvent.preset ?? 'credentials', {
      onFilesCreated,
    });
  }, [
    createSecretFileEvent.sequence,
    createSecretFileEvent.preset,
    filesActions.handleCreateSecretFile,
    onFilesCreated,
  ]);

  /** Store note-file token: opens markdown note creation once per token bump. */
  useEffect(() => {
    if (createNoteFileToken === 0) {
      return;
    }
    if (createNoteFileToken === lastHandledCreateNoteFileTokenRef.current) {
      return;
    }
    lastHandledCreateNoteFileTokenRef.current = createNoteFileToken;
    filesActions.handleCreateNoteFile({
      onFilesCreated,
    });
  }, [createNoteFileToken, filesActions.handleCreateNoteFile, onFilesCreated]);

  /** Global `onFilesUpdated` bus: merges remote updates into this list. */
  useEffect(() => {
    const unsubscribeOnFilesUpdated = useAppStore
      .getState()
      .events.on.onFilesUpdated(({ files }) => {
        onFilesUpdated(files);
      });
    return unsubscribeOnFilesUpdated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFilesUpdated, parentFolder]);

  /** Global `onFilesCreated` bus: prepends folders/files created outside this list (e.g. picker). */
  useEffect(() => {
    const unsubscribeOnFilesCreated = useAppStore
      .getState()
      .events.on.onFilesCreated(({ files }) => {
        onFilesCreated(files);
      });
    return unsubscribeOnFilesCreated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFilesCreated, parentFolder]);

  /** Applies read-state patches broadcast when another UI marks files read without waiting for refetch. */
  useEffect(() => {
    return useAppStore.getState().events.on.filesMarkedAsReadLocally(({ fileIds }) => {
      patchFilesMarkedReadLocally(fileIds);
    });
  }, [patchFilesMarkedReadLocally]);

  /** Global `onFilesDeleted` bus: removes rows cleared elsewhere in the app. */
  useEffect(() => {
    const unsubscribeOnFilesDeleted = useAppStore
      .getState()
      .events.on.onFilesDeleted(({ files }) => {
        onFilesDeleted(files);
      });
    return unsubscribeOnFilesDeleted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFilesDeleted, parentFolder]);

  /** Clears `isPublicShare` locally when a public link is revoked from another surface. */
  useEffect(() => {
    const unsubscribePublicLinkRevoked = useAppStore
      .getState()
      .events.on.onFilePublicLinkRevoked(({ fileId }) => {
        setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, isPublicShare: false } : f)));
      });
    return unsubscribePublicLinkRevoked;
  }, []);

  /** Path change: drop stale multi-selection keys that would not match the new folder. */
  const listPathname = stripPreviewPath(location.pathname);
  useEffect(() => {
    setSelectedTableKeys([]);
  }, [listPathname]);

  /**
   * Record manual scroll times so programmatic scroll-to-top does not interrupt the user mid-scroll.
   * (Scrollbar show/hide is handled by OverlayScrollbars autoHide on the table body.)
   */
  useEffect(() => {
    const body = document.querySelector<HTMLElement>('.ant-table-body');
    const scrollElement = getAntTableScrollElement(body);
    if (!scrollElement) return;

    const handleManualScroll = () => {
      lastManualTableScrollRef.current = Date.now();
      if (pendingTableScrollTopTimeoutRef.current) {
        clearTimeout(pendingTableScrollTopTimeoutRef.current);
        pendingTableScrollTopTimeoutRef.current = null;
      }
    };
    scrollElement.addEventListener('scroll', handleManualScroll);
    return () => {
      scrollElement.removeEventListener('scroll', handleManualScroll);
      if (pendingTableScrollTopTimeoutRef.current)
        clearTimeout(pendingTableScrollTopTimeoutRef.current);
    };
  }, [tableScrollY]);

  return {
    files,
    totalFiles,
    hasMore,
    isLoading,
    isNavigating,
    filters,
    setFilters,
    rowSelection,
    selectedTableKeys,
    setSelectedTableKeys,
    handleRowContextMenu,
    handleTableChange,
    handleRowClick,
    handleLoadMore,
    breadcrumbItems,
    isFilesListInGridMode,
    setIsFilesListInGridMode,
    isContextMenuVisible,
    setIsContextMenuVisible,
    contextMenuPosition,
    setContextMenuPosition,
    isFilesContextMenuVisible,
    setIsFilesContextMenuVisible,
    filesContextMenuPosition,
    setFilesContextMenuPosition,
    handleEmptyAreaContextMenu,
    selectedFiles,
    handleFileRowClick,
    handleFileRowDoubleClick,
    isUploadDisabledInCurrentFolder,
    emptyState,
    contextMenuFileActions,
    topBarFileActions,
    filesActionsContextBase,
    handleFiltersChange,
    tableScrollY,
    gridScrollY,
    handleMoveDraggedFiles,
    activeFiltersCount,
    parentFolder,
    table,
    authenticatedUser,
    token,
    refreshToken,
    message,
    t,
    setFiles,
    setTotalFiles,
    setPrependFileData,
    onFilesCreated,
    onFilesUpdated,
    onFilesDeleted,
    isInFileRoute,
    setIsNavigating,
    openFolderOrRequestPassword,
    openFileSafely,
    copyFileLinkToClipboard,
    openRightPanel,
    getFilePasswordSession,
    handleDownloadFiles,
    filesActions,
  };
}
